/**
 * What the grip step (S1a / S2a) shows: the **shapes** a hand takes in a
 * phrase, each with the places it takes them.
 *
 * ## The unit is an occupation, and the analyzer decides it
 *
 * A grip is a maximal stretch during which **no finger changes key**. That
 * is a fact about the piece and its fingering, so `analyzer-go` segments it
 * and publishes it; this module never re-derives it.
 *
 * The rule this replaced segmented on *reach* — a range grew for as long as
 * everything the hand had touched still fitted under one hand. That
 * certifies a span, it does not assert an occupation: the hand *could* be
 * there is not the hand *is* there. On the left hand of *Libérée délivrée*
 * it produced one region of four keys, `{A#2, D3, F3, A3}`, over two bars in
 * which the hand actually played two fifths a third apart and moved between
 * them. The panel drew a region that had existed at no instant, and said
 * "2 notes se jouent d'ici" over four lit keys — the count was right, the
 * region was fiction, and the two contradicted each other on screen.
 *
 * ## Why shapes and not grips
 *
 * Occupation segmentation is finer, and shown raw it would hand the player
 * a queue of short objects to click through — the note-by-note step this
 * whole screen replaced. The escape is that **the fine segmentation is
 * true and the presentation deduplicates it**: four fifths fingered 5-1 at
 * four pitches are one shape at four places, not four things to learn.
 *
 * So the cap is on *distinct shapes*, never on occurrences. A hand that
 * repeats one grip across a bar line has one thing to show however many
 * times it does it, and a hand with a different configuration every attack
 * has nothing to show and says so.
 *
 * ## The fingering is part of the shape, and safe to display
 *
 * Inside a grip the finger-to-key assignment is constant *by construction* —
 * that is what the segmentation rule says. So a still panel can state it
 * without freezing a hand that reconfigures, and it cannot contradict the
 * roll, since it is read off the roll.
 *
 * An earlier version of this screen showed no finger numbers, on the grounds
 * that an assignment lives a median of 3 attacks against a position's 7. That
 * 7 does not reproduce: re-measured on 450 engraved scores it is **3 against
 * 3**, so the contrast the rule rested on was not there.
 *
 * The assignment is a function from finger to key and not the reverse: two
 * fingers share a key on 8.1% of grips, a substitution on a repeated note.
 * Deciding what to write on such a key is this module's job, not the
 * analyzer's — see `ShapeKey`.
 *
 * Pure TS: no React, no Studio, no DOM.
 */

import type { Phrase, PhraseGrip } from './types';

/**
 * Above this many **distinct shapes**, the hand is not settling into
 * anything the step can name, and it says so rather than drawing a list.
 *
 * Counted on shapes and not on grips: the whole point of deduplicating is
 * that occurrences are free. A hand taking one shape at six places is one
 * screen; a hand taking six shapes is a hand that travels.
 *
 * Four and not three. Measured per (work unit × hand) on 450 engraved
 * scores, three covers 49.7% of pairs and four covers 64.8% — three is not
 * the loose setting, it silences half of them for 15.1 points. Read against
 * its base rate: the same cap of three on a requantified corpus covers only
 * 19.7%, so this number describes our repertoire, not a property of hands.
 *
 * The unit matters more than the cap anyway: the same cap of three applied
 * to an analyzer phrase instead of a work unit covers 24.0%, so cutting the
 * phrase down bought more than any threshold here can.
 */
const MAX_SHAPES = 4;

/**
 * Total **keys** the step may ask for, across every shape and every place.
 *
 * A second budget, and the cap above is not it. `MAX_SHAPES` counts **new
 * material** — configurations to retain — while re-taking a known shape
 * somewhere else is repetition, which is cheap and wanted. Without this,
 * four shapes at four places each lands on exactly the dense units that are
 * already the most loaded.
 *
 * ## Counted in keys, and it has to be
 *
 * It counted *places*, back when a place was one gesture: the whole shape
 * struck at once. A place is now walked key by key over a held opening, so
 * it costs as many strikes as it has keys, and the branch averages **3.30**
 * of them. Six places was therefore about twenty strikes on a loaded unit,
 * which made the prise **longer than the practice run it prepares** (median
 * 8 groups). A preparatory step that outgrows the step it prepares has
 * stopped preparing anything.
 *
 * Eight keys sits just above the cost of two ordinary shapes, so the common
 * unit keeps its repetition and the loaded one stops accumulating.
 *
 * Spent in the same order as before: one place per shape first — that is
 * the material, and it is never rationed — then whatever is left goes to
 * extra places, most-recurring shape first, since the shape that repeats
 * most is the one whose transposition is worth drilling.
 */
const MAX_KEYS = 8;

/** Under two keys there is no shape, only a note. */
const MIN_KEYS = 2;

/**
 * Span past which a stretch the hand cannot hold is **named** rather than
 * only dropped.
 *
 * Dropping a lone key hides something *easier* than what is shown, which
 * costs nothing. Dropping a nineteen-semitone reach hides something
 * *harder*, and leaves the player believing the unit is three comfortable
 * shapes until it is not — then reading the wall they hit as "I am bad"
 * rather than "this passage is hard". Naming it in advance is what moves
 * that attribution, and mis-attribution is a first-order cause of adults
 * giving up.
 *
 * Fifteen and not twelve: a ninth is within many adult hands, and
 * announcing the impossible about something possible is melodramatic.
 * Below this, dropped in silence.
 */
const WIDE_GAP_SEMITONES = 15;

// ── Public types ──────────────────────────────────────────────────

/** One key of a shape: where it sits, and what is written on it. */
export type ShapeKey = {
  /** Semitones above the lowest key of the shape. */
  readonly offset: number;
  /**
   * Fingers on this key, ascending. Normally one; two is a substitution on
   * a repeated note, which the panel labels with both rather than picking a
   * winner — the analyzer reports what the fingering says and leaves the
   * label to whoever draws it.
   */
  readonly fingers: readonly number[];
};

/**
 * A stretch of the unit the hand cannot hold: the keys are further apart
 * than five fingers reach.
 *
 * Not a shape, so not shown as one — and wide enough to be worth saying out
 * loud, which a lone dropped key is not.
 */
export type WideGap = {
  readonly startMeasure: number;
  readonly endMeasure: number;
  readonly lowMidi: number;
  readonly highMidi: number;
  /** Semitones from the lowest key to the highest. */
  readonly span: number;
};

/** One hand configuration, together with everywhere the phrase uses it. */
export type HandShape = {
  /** The analyzer's identity for this configuration, e.g. `"5:0,1:7"`. */
  readonly id: string;
  /**
   * The configuration, ascending by pitch — what to draw.
   *
   * Derived from any one occurrence, since they all take the same shape.
   * `id` is what decides sameness; this is only how it is shown.
   */
  readonly keys: readonly ShapeKey[];
  /** Every grip in the phrase that takes this shape, chronological. */
  readonly occurrences: readonly PhraseGrip[];
  /**
   * How many of those places the step actually asks for, 1 or more.
   *
   * Never `occurrences.length` blindly: the presses of the whole unit share
   * one budget, so a shape that recurs eleven times gets what is left after
   * every other shape has been taken once. The count the player is *told*
   * stays the true one — what is rationed is what is asked for.
   */
  readonly asked: number;
};

/**
 * `empty`       , the hand does not play in the phrase: the step is skipped.
 * `shapes`      , 1 to `MAX_SHAPES` configurations, first-use order.
 * `orientation` , the hand never settles into few enough shapes to name, so
 *                 the step gives the starting key and the territory instead.
 *
 * There is deliberately no third answer for "a hand that plays but has no
 * shape". A step that renders nothing is worse than every failure it was
 * built to avoid: the player is sent to practice without being told where
 * to put their hand, and the stepper that would let them go back is inside
 * the panel that did not render.
 *
 * `orientation` is never false, because it asserts no simultaneity — a
 * range of pitch and a first key are true of any hand in any unit. It also
 * answers the question that actually matters to a hand about to play: *am I
 * going to have to move?*
 */
export type HandShapePlan =
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'shapes';
      readonly shapes: readonly HandShape[];
      readonly wideGaps: readonly WideGap[];
    }
  | {
      readonly kind: 'orientation';
      /** The grip the hand opens on: where the step starts the player. */
      readonly firstGrip: PhraseGrip;
      /** Lowest and highest key the hand touches in the unit. */
      readonly lowMidi: number;
      readonly highMidi: number;
      readonly wideGaps: readonly WideGap[];
      /** Distinct shapes the hand needed, for telemetry and debug. */
      readonly shapesNeeded: number;
    };

/** Where a shape's places sit relative to each other, for the copy. */
export type ShapeDirection = 'still' | 'down' | 'up' | 'mixed';

// ── Build ─────────────────────────────────────────────────────────

/** Apply the journey's dosage to the analyzer's grips for one hand. */
export function buildHandShapePlan(
  grips: readonly PhraseGrip[],
): HandShapePlan {
  // A grip clipped to this unit can cover ticks during which the hand
  // strikes nothing — it was placed here for what it plays elsewhere.
  // Those are not places the player is taken to.
  const playing = grips.filter((g) => g.attacks > 0);

  if (playing.length === 0) {
    return { kind: 'empty' };
  }

  const wideGaps = playing.filter((g) => !g.holdable).map(toWideGap);

  // Discarded, exactly like a lone key — not a reason to silence the unit.
  //
  // An earlier rule let one unholdable stretch make the whole hand
  // `travelling`, on the grounds that the step must not prepare a hand for
  // a unit containing a moment it never mentions. That over-applied a rule
  // about *drawing* things that never existed: this step does not promise
  // to describe everything the hand does, it promises to show the shapes
  // the hand takes, and a nineteen-semitone reach is no more a shape than a
  // single key is. Measured, the strict rule silenced 20.7% of the
  // (unit × hand) pairs of a dense piece against 2.7% on the catalogue.
  //
  // What survives of the concern is `wideGaps`: dropped, but named.
  const shaped = playing.filter(
    (g) => g.holdable && g.midis.length >= MIN_KEYS,
  );

  if (shaped.length === 0) {
    return orientationOf(playing, wideGaps, 0);
  }

  const shapes = groupByShape(shaped);

  if (shapes.length > MAX_SHAPES) {
    return orientationOf(playing, wideGaps, shapes.length);
  }

  return { kind: 'shapes', shapes: allocatePresses(shapes), wideGaps };
}

/**
 * Spend `MAX_KEYS` over the shapes: one place each, then the remainder to
 * extra places, most-recurring shape first.
 *
 * Most-recurring first because that is where a second place buys the most:
 * a configuration the unit uses five times is one whose transposition the
 * hand will need, while a shape used once has nothing more to say after
 * being taken.
 */
function allocatePresses(
  shapes: readonly Omit<HandShape, 'asked'>[],
): HandShape[] {
  const asked = shapes.map(() => 1);
  // What one more place of each shape costs, in keys.
  const cost = shapes.map((shape) => Math.max(1, shape.keys.length));
  let left = MAX_KEYS - cost.reduce((total, keys) => total + keys, 0);

  const byFrequency = shapes
    .map((shape, i) => ({ i, places: shape.occurrences.length }))
    .sort((a, b) => b.places - a.places || a.i - b.i);

  // Round-robin over the frequent shapes rather than draining the first:
  // two shapes at three places each should get two presses apiece, not
  // three and one.
  let progressed = true;

  while (left > 0 && progressed) {
    progressed = false;

    for (const { i, places } of byFrequency) {
      if (left <= 0) {
        break;
      }

      if (asked[i] >= places || cost[i] > left) {
        continue;
      }

      asked[i] += 1;
      left -= cost[i];
      progressed = true;
    }
  }

  return shapes.map((shape, i) => ({ ...shape, asked: asked[i] }));
}

function orientationOf(
  playing: readonly PhraseGrip[],
  wideGaps: readonly WideGap[],
  shapesNeeded: number,
): HandShapePlan {
  const midis = playing.flatMap((g) => g.midis);
  const firstGrip = playing.reduce((best, g) =>
    g.startTick < best.startTick ? g : best,
  );

  return {
    kind: 'orientation',
    firstGrip,
    lowMidi: Math.min(...midis),
    highMidi: Math.max(...midis),
    wideGaps,
    shapesNeeded,
  };
}

function toWideGap(grip: PhraseGrip): WideGap {
  const lowMidi = Math.min(...grip.midis);
  const highMidi = Math.max(...grip.midis);

  return {
    startMeasure: grip.startMeasure,
    endMeasure: grip.endMeasure,
    lowMidi,
    highMidi,
    span: highMidi - lowMidi,
  };
}

/** Gaps worth saying out loud, widest first. */
export function namedGaps(gaps: readonly WideGap[]): readonly WideGap[] {
  return [...gaps]
    .filter((g) => g.span >= WIDE_GAP_SEMITONES)
    .sort((a, b) => b.span - a.span);
}

/**
 * Plans déjà construits, par phrase puis par main.
 *
 * `PhraseFSM` demande `hasShapeStep` cinq ou six fois par mutation, et
 * chaque appel reconstruisait le plan entier. Une phrase est immuable et
 * vit aussi longtemps que son FSM : la `WeakMap` suit sa durée de vie.
 */
const plansByPhrase = new WeakMap<
  Phrase,
  { left?: HandShapePlan; right?: HandShapePlan }
>();

/** The plan for one hand of one phrase. */
export function phraseHandShapePlan(
  phrase: Phrase,
  hand: 'left' | 'right',
): HandShapePlan {
  let plans = plansByPhrase.get(phrase);

  if (plans === undefined) {
    plans = {};
    plansByPhrase.set(phrase, plans);
  }

  let plan = plans[hand];

  if (plan === undefined) {
    plan = buildHandShapePlan(phrase.gripsByHand[hand]);
    plans[hand] = plan;
  }

  return plan;
}

/**
 * Whether the grip step runs at all for this hand.
 *
 * False only on a hand that does not play here. A hand that never settles
 * still gets the step, as orientation: it is told where it starts and how
 * far it ranges. The alternative was a panel that renders nothing, which
 * sends the player to practice with no idea where to put their hand and
 * hides the stepper that would let them go back.
 */
export function hasShapeStep(phrase: Phrase, hand: 'left' | 'right'): boolean {
  return phraseHandShapePlan(phrase, hand).kind !== 'empty';
}

/**
 * Below this many attacks, the practice step has nothing to walk.
 *
 * One. A run of a single attack is not a run: the player presses one key,
 * the roll does not move, and the screen that closes the pass offers to do
 * it again to memorise it. The hand is not left unserved either — the grip
 * step, which always runs, has just had them find that key and put a finger
 * on it, and the run in time will ask for it where it actually matters,
 * which is *when* it comes in under the other hand.
 *
 * Attacks and not notes: a chord struck once is one attack, and the grip
 * step has just had the hand take those very keys together.
 */
const MIN_PRACTICE_ATTACKS = 2;

/**
 * Whether the practice step runs at all for this hand.
 *
 * `both` is the two-hands run (`S3`), which is about coordination and stays
 * worth doing when one of the two hands carries a single note.
 */
export function hasPracticeStep(
  phrase: Pick<Phrase, 'notesByHand'>,
  hand: 'left' | 'right' | 'both',
): boolean {
  return attackCount(phrase, hand) >= MIN_PRACTICE_ATTACKS;
}

/**
 * Distinct instants this hand strikes at.
 *
 * The same count `buildPracticeGroups` arrives at, which is what the run
 * walks. Kept here rather than imported from the view: the FSM decides
 * whether a step exists, and it cannot depend on a component.
 */
export function attackCount(
  phrase: Pick<Phrase, 'notesByHand'>,
  hand: 'left' | 'right' | 'both',
): number {
  const ticks = new Set<number>();

  if (hand !== 'right') {
    for (const note of phrase.notesByHand.left) {
      ticks.add(note.startTick);
    }
  }

  if (hand !== 'left') {
    for (const note of phrase.notesByHand.right) {
      ticks.add(note.startTick);
    }
  }

  return ticks.size;
}

/**
 * One thing the step asks the hand to put down, in order.
 *
 * The unit of the FSM's `positionIndex`. Flattening shapes × places here
 * rather than walking places inside the view means going back a step, or
 * rehydrating a saved progression, lands on the same press it left — a
 * local walk in the component would silently restart at the first place.
 */
export type ShapePress = {
  readonly shape: HandShape;
  readonly place: PhraseGrip;
  readonly shapeIndex: number;
  /** Which place of that shape, 0-based, and how many are asked for. */
  readonly placeIndex: number;
  readonly placeCount: number;
};

/** Every press of the step, in order. Empty for a hand that does not play. */
export function shapePresses(plan: HandShapePlan): readonly ShapePress[] {
  if (plan.kind !== 'shapes') {
    return [];
  }

  const out: ShapePress[] = [];

  plan.shapes.forEach((shape, shapeIndex) => {
    for (let placeIndex = 0; placeIndex < shape.asked; placeIndex += 1) {
      out.push({
        shape,
        place: shape.occurrences[placeIndex],
        shapeIndex,
        placeIndex,
        placeCount: shape.asked,
      });
    }
  });

  return out;
}

/**
 * How many times the step asks this hand to put keys down before it is done.
 *
 * One shape taken at three places is three; an orientation is one, since
 * the player still has to find its starting key.
 */
export function shapeStepCount(plan: HandShapePlan): number {
  if (plan.kind === 'orientation') {
    return 1;
  }

  return shapePresses(plan).length;
}

/**
 * Which way a shape's places move.
 *
 * `still` when it is taken once, or taken repeatedly without the hand
 * moving; `mixed` when it goes both ways, which is the case where naming a
 * direction in the copy would be a lie.
 */
export function shapeDirection(shape: HandShape): ShapeDirection {
  let down = false;
  let up = false;

  for (let i = 1; i < shape.occurrences.length; i += 1) {
    const delta =
      lowestOf(shape.occurrences[i]) - lowestOf(shape.occurrences[i - 1]);

    if (delta < 0) {
      down = true;
    }

    if (delta > 0) {
      up = true;
    }
  }

  if (down && up) {
    return 'mixed';
  }

  if (down) {
    return 'down';
  }

  if (up) {
    return 'up';
  }

  return 'still';
}

/** The lowest key of every place this shape is taken, chronological. */
export function shapeRoots(shape: HandShape): readonly number[] {
  return shape.occurrences.map(lowestOf);
}

// ── Internals ─────────────────────────────────────────────────────

function lowestOf(grip: PhraseGrip): number {
  return Math.min(...grip.midis);
}

/**
 * Fold grips onto their configuration, keeping first-use order.
 *
 * First-use and not most-used: the panel walks the phrase, and a shape
 * introduced later must not be presented before one the hand already took.
 */
function groupByShape(
  grips: readonly PhraseGrip[],
): Omit<HandShape, 'asked'>[] {
  const byId = new Map<string, PhraseGrip[]>();

  for (const grip of grips) {
    const found = byId.get(grip.shape);

    if (found === undefined) {
      byId.set(grip.shape, [grip]);

      continue;
    }

    found.push(grip);
  }

  return [...byId.entries()].map(([id, occurrences]) => ({
    id,
    keys: shapeKeysOf(occurrences[0]),
    occurrences,
  }));
}

/**
 * How to draw one grip: one entry per key, carrying every finger on it.
 *
 * The analyzer publishes finger→key pairs, which is the direction that is a
 * function; a key with two fingers on it appears twice there and once here,
 * labelled with both.
 */
function shapeKeysOf(grip: PhraseGrip): ShapeKey[] {
  const lowest = lowestOf(grip);
  const byOffset = new Map<number, number[]>();

  for (const { finger, midi } of grip.fingers) {
    const offset = midi - lowest;
    const found = byOffset.get(offset);

    if (found === undefined) {
      byOffset.set(offset, [finger]);

      continue;
    }

    found.push(finger);
  }

  return [...byOffset.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([offset, fingers]) => ({
      offset,
      fingers: [...fingers].sort((a, b) => a - b),
    }));
}
